/**
 * HELPERS PARA ESTADÍSTICAS GLOBALES DE CONTRATOS
 * Calcula: Pagado, Deuda, Corriente, Futuro
 */

import { classifyPayment } from "./paymentHelpers";

/**
 * Calcula estadísticas globales de TODOS los contratos
 * Entrada: array de pagos de TODOS los contratos
 * Salida: stats completas con totales y derivados
 */
export const calculateGlobalContractStats = (allPayments) => {
	if (!allPayments || allPayments.length === 0) {
		return {
			pagado: { count: 0, total: 0 },
			deuda: { count: 0, total: 0 },
			corriente: { count: 0, total: 0 },
			futuro: { count: 0, total: 0 },
			totalPagado: 0,
			totalDeuda: 0,
			totalCorriente: 0,
			totalFuturo: 0,
			totalGeneral: 0,
			debeAhora: 0, // deuda + corriente
			esperaGanar: 0, // futuro (próximos cobros)
			percentPagado: 0,
			percentDeuda: 0,
			percentCorriente: 0,
			percentFuturo: 0,
			hayDeuda: false,
		};
	}

	// Acumular stats
	const stats = {
		pagado: { count: 0, total: 0 },
		deuda: { count: 0, total: 0 },
		corriente: { count: 0, total: 0 },
		futuro: { count: 0, total: 0 },
	};

	// Clasificar cada pago
	allPayments.forEach((pago) => {
		const clasificacion = classifyPayment(pago);
		stats[clasificacion].count += 1;
		stats[clasificacion].total += pago.payment;
	});

	// Calcular totales
	const totalPagado = stats.pagado.total;
	const totalDeuda = stats.deuda.total;
	const totalCorriente = stats.corriente.total;
	const totalFuturo = stats.futuro.total;
	const totalGeneral = totalPagado + totalDeuda + totalCorriente + totalFuturo;

	// Derivados importantes
	const debeAhora = totalDeuda + totalCorriente; // Lo que debe pagar HOY
	const esperaGanar = totalFuturo; // Lo que espera cobrar en próximos meses

	// Porcentajes
	const percentPagado = totalGeneral > 0 ? Math.round((totalPagado / totalGeneral) * 100) : 0;
	const percentDeuda = totalGeneral > 0 ? Math.round((totalDeuda / totalGeneral) * 100) : 0;
	const percentCorriente = totalGeneral > 0 ? Math.round((totalCorriente / totalGeneral) * 100) : 0;
	const percentFuturo = totalGeneral > 0 ? Math.round((totalFuturo / totalGeneral) * 100) : 0;

	const hayDeuda = totalDeuda > 0 || totalCorriente > 0;

	return {
		...stats,
		totalPagado,
		totalDeuda,
		totalCorriente,
		totalFuturo,
		totalGeneral,
		// LO IMPORTANTE
		debeAhora, // Cuanto debe pagar ahora mismo
		esperaGanar, // Cuanto espera ganar en próximos meses
		// Porcentajes
		percentPagado,
		percentDeuda,
		percentCorriente,
		percentFuturo,
		// Flags
		hayDeuda,
	};
};

/**
 * Obtiene todos los pagos de todos los contratos
 * Combina: contratos array con pagos array
 */
export const getAllPaymentsFromContracts = (contracts, payments) => {
	// Si vienen los pagos directos, retornarlos
	if (payments && payments.length > 0) {
		return payments;
	}

	// Si no, extraer de los contratos
	let allPayments = [];
	if (contracts && contracts.length > 0) {
		contracts.forEach((contract) => {
			if (contract.pagos && contract.pagos.length > 0) {
				allPayments = [...allPayments, ...contract.pagos];
			}
		});
	}

	return allPayments;
};

/**
 * Resumen amigable de las stats
 */
export const getGlobalStatsSummary = (stats) => {
	return {
		pagado: {
			label: "PAGADO",
			count: stats.pagado.count,
			total: stats.totalPagado,
			icon: "✓",
			color: "green",
			percent: stats.percentPagado,
		},
		deuda: {
			label: "DEUDA",
			count: stats.deuda.count,
			total: stats.totalDeuda,
			icon: "⏰",
			color: "red",
			percent: stats.percentDeuda,
		},
		corriente: {
			label: "CORRIENTE",
			count: stats.corriente.count,
			total: stats.totalCorriente,
			icon: "📅",
			color: "yellow",
			percent: stats.percentCorriente,
		},
		futuro: {
			label: "FUTURO (Espera Ganar)",
			count: stats.futuro.count,
			total: stats.totalFuturo,
			icon: "📆",
			color: "blue",
			percent: stats.percentFuturo,
		},
	};
};

export default {
	calculateGlobalContractStats,
	getAllPaymentsFromContracts,
	getGlobalStatsSummary,
};